import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { joinLeague, getLeaguePreview } from '../services/api';
import getErrorMessage from '../utils/errorHandler';
import '../styles/app/auth.css';
import '../styles/app/forms.css';
import { HIDE_MONEY } from '../utils/platform';

function JoinPool() {
  const navigate = useNavigate();
  const { code: urlCode } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [poolPreview, setPoolPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Get code from URL path (/join/ABC123) or query param (/join?code=ABC123)
  useEffect(() => {
    const code = urlCode || searchParams.get('code');
    if (code) {
      setInviteCode(code.toUpperCase());
      fetchPoolPreview(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode, searchParams]);

  const fetchPoolPreview = async (code) => {
    if (!code || code.trim().length < 4) return;

    setPreviewLoading(true);
    setError('');
    setPoolPreview(null);

    try {
      const response = await getLeaguePreview(code.trim().toUpperCase());
      const preview = response.data;

      // If user is logged in and already a member, redirect to the pool
      if (isAuthenticated && preview.is_member) {
        navigate(`/pool/${preview.id}`);
        return;
      }

      setPoolPreview(preview);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Invalid invite code. Please check and try again.');
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setInviteCode(code);
    setError('');
    setPoolPreview(null);
  };

  const handleLookup = (e) => {
    e.preventDefault();
    if (inviteCode.trim()) {
      fetchPoolPreview(inviteCode);
    }
  };

  const handleJoin = async () => {
    // If not logged in, redirect to login with return URL
    if (!isAuthenticated) {
      const returnUrl = urlCode ? `/join/${inviteCode}` : `/join?code=${inviteCode}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await joinLeague(inviteCode.trim().toUpperCase());
      navigate(`/pool/${response.data.league.id}`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pnp-auth pnp-join">
      <div className="pnp-auth-stage" aria-hidden="true">
        <span className="pnp-auth-beam" />
        <span className="pnp-auth-orb pnp-auth-orb-red" />
        <span className="pnp-auth-orb pnp-auth-orb-blue" />
        <svg className="pnp-auth-cage pnp-auth-cage-1" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
        <svg className="pnp-auth-cage pnp-auth-cage-2" viewBox="0 0 100 100"><path d="M29.3 1h41.4L99 29.3v41.4L70.7 99H29.3L1 70.7V29.3z" /></svg>
      </div>

      <div className="pnp-auth-grid">
        <header className="pnp-join-head pnp-rise">
          <p className="pnp-eyebrow">{poolPreview ? 'Invite' : 'Invite code'}</p>
          <h1 className="pnp-join-title">Join a <span className="pnp-punch">Pool</span></h1>
          <p className="pnp-join-sub">
            {poolPreview ? 'You\'ve been invited to join:' : 'Enter the invite code you received'}
          </p>
        </header>

        <section className={`pnp-auth-card pnp-rise${poolPreview ? ' pnp-join-bare' : ''}`} style={{ '--i': 1 }}>
          {error && (
            <div className="message message-error" role="alert">
              {error}
            </div>
          )}

          {/* Pool Preview Card */}
          {poolPreview && (
            <div className="pnp-join-preview">
              <span className="pnp-claw" aria-hidden="true" />
              <p className="pnp-eyebrow">You're invited</p>
              <h2 className="pnp-join-name">{poolPreview.name}</h2>
              {poolPreview.description && (
                <p className="pnp-join-desc">
                  {poolPreview.description}
                </p>
              )}
              <div className="pnp-join-stats" style={HIDE_MONEY ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
                {!HIDE_MONEY && (
                  <div className="pnp-join-stat">
                    <span className="pnp-join-stat-label">Entry Fee</span>
                    <span className="pnp-join-stat-value is-red">
                      ${poolPreview.entry_fee}
                    </span>
                  </div>
                )}
                <div className="pnp-join-stat">
                  <span className="pnp-join-stat-label">Members</span>
                  <span className="pnp-join-stat-value">
                    {poolPreview.member_count}
                  </span>
                </div>
              </div>
              {poolPreview.commissioner && poolPreview.commissioner !== 'Unknown' && (
                <p className="pnp-join-commish">
                  <span className="pnp-join-avatar" aria-hidden="true">{String(poolPreview.commissioner).charAt(0)}</span>
                  <span>Commissioner: <strong>{poolPreview.commissioner}</strong></span>
                </p>
              )}

              <button
                className="btn btn-primary btn-lg btn-block"
                onClick={handleJoin}
                disabled={loading}
              >
                {loading ? 'Joining...' : isAuthenticated ? 'Join Pool' : 'Sign in to Join'}
              </button>

              {!isAuthenticated && (
                <p className="pnp-join-signup">
                  Don't have an account?{' '}
                  <Link
                    to={`/register?redirect=${encodeURIComponent(urlCode ? `/join/${inviteCode}` : `/join?code=${inviteCode}`)}`}
                  >
                    Create one
                  </Link>
                </p>
              )}
            </div>
          )}

          {/* Code Input (shown when no preview or looking up) */}
          {!poolPreview && (
            <form onSubmit={handleLookup}>
              <div className="form-group">
                <label className="form-label" htmlFor="join-code">Invite Code</label>
                <input
                  id="join-code"
                  type="text"
                  className="form-input pnp-join-code"
                  value={inviteCode}
                  onChange={handleCodeChange}
                  placeholder="e.g., ABC123"
                  maxLength="8"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck="false"
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg btn-block"
                disabled={previewLoading || !inviteCode.trim()}
              >
                {previewLoading && (
                  <span aria-hidden="true" className="pnp-join-spinner" />
                )}
                {previewLoading ? 'Looking up…' : 'Find Pool'}
              </button>
            </form>
          )}

          {/* Different code button (when viewing preview) */}
          {poolPreview && (
            <button
              className="btn btn-secondary btn-block"
              onClick={() => {
                setPoolPreview(null);
                setInviteCode('');
                setError('');
              }}
            >
              Use Different Code
            </button>
          )}

          <div className="pnp-join-own">
            <p>
              Want to start your own pool?
            </p>
            <button
              className="btn btn-outline"
              onClick={() => navigate(isAuthenticated ? '/create-pool' : `/login?redirect=${encodeURIComponent('/create-pool')}`)}
            >
              Create a Pool
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default JoinPool;
